import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Spinner } from "@/components/ui/Spinner";
import { getAgaMobileUploadQr } from "@/lib/angebotsanfragen";
import { ApiError } from "@/lib/apiClient";

export interface AgaMobileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  agaId: number | null;
  /** Wird aufgerufen, wenn vom Handy neue Anhänge hochgeladen wurden. */
  onUploaded?: () => void;
}

const POLL_MS = 2000;

export function AgaMobileUploadDialog({
  open,
  onClose,
  agaId,
  onUploaded,
}: AgaMobileUploadDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadQr = useCallback(async () => {
    if (!agaId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAgaMobileUploadQr(agaId);
      setQrUri(data.qr_data_uri || null);
      setQrUrl(data.url || null);
      setExpiresAt(data.expires_at || null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "QR-Code konnte nicht erzeugt werden.";
      setError(message);
      setQrUri(null);
      setQrUrl(null);
    } finally {
      setLoading(false);
    }
  }, [agaId]);

  useEffect(() => {
    if (!open || !agaId) {
      stopPoll();
      return;
    }
    void loadQr();
    return () => {
      stopPoll();
    };
  }, [open, agaId, loadQr, stopPoll]);

  useEffect(() => {
    if (!open || !agaId || !onUploaded) {
      stopPoll();
      return;
    }

    stopPoll();
    pollRef.current = setInterval(() => {
      onUploaded();
    }, POLL_MS);

    return () => {
      stopPoll();
    };
  }, [open, agaId, onUploaded, stopPoll]);

  const expiresLabel = expiresAt
    ? new Date(expiresAt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Vom Handy hochladen"
      description="QR-Code mit dem Smartphone scannen. Auf dem Handy können Fotos und Dokumente hochgeladen werden – sie erscheinen hier automatisch."
      size="md"
      zIndexClass="z-[140]"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <Spinner />
            <span className="text-sm text-label-secondary">QR-Code wird erzeugt …</span>
          </div>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {qrUri ? (
          <div className="flex flex-col items-center gap-3 rounded-ios-lg border border-separator bg-fill-tertiary/60 px-4 py-5">
            <ClickableQrCode
              src={qrUri}
              href={qrUrl}
              alt="QR-Code für Handy-Upload"
              className="h-44 w-44 rounded-ios border border-separator bg-white p-2 shadow-sm"
            />
            <p className="text-center text-xs text-label-secondary">
              QR-Code mit dem Smartphone scannen
            </p>
            {qrUrl ? (
              <a
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-center text-xs text-accent"
              >
                {qrUrl}
              </a>
            ) : null}
            {expiresLabel ? (
              <p className="text-center text-xs text-label-tertiary">
                Link gültig bis {expiresLabel} Uhr
              </p>
            ) : null}
          </div>
        ) : null}

        <p className="text-xs text-label-tertiary">
          Unterstützt: Fotos (PNG, JPEG, WebP, HEIC) und PDF-Dateien bis 10 MB.
          Neue Anhänge werden automatisch angezeigt.
        </p>
      </div>
    </Dialog>
  );
}
